import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Row, Typography, Space, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ToolOutlined,
  CarOutlined,
  SettingOutlined,
  CalendarOutlined,
  TeamOutlined,
  FormOutlined,
  BgColorsOutlined,
} from '@ant-design/icons';
import Button from '../../components/common/Button';

const { Title, Paragraph, Text } = Typography;

type HubItem = {
  key: string;
  titleKey: string;
  subtitleKey: string;
  descriptionKey: string;
  path: string;
  icon: React.ReactNode;
  tagKeys: string[];
};

const hubSections: Array<{ title: string; items: HubItem[] }> = [
  {
    title: 'machinery.hub.operation',
    items: [
      {
        key: 'machines',
        titleKey: 'machinery.hub.cards.machines.title',
        subtitleKey: 'machinery.hub.cards.machines.subtitle',
        descriptionKey: 'machinery.hub.cards.machines.description',
        path: '/machinery/machines',
        icon: <ToolOutlined />,
        tagKeys: ['machinery.hub.tags.list', 'machinery.hub.tags.detail'],
      },
      {
        key: 'vehicles',
        titleKey: 'machinery.hub.cards.vehicles.title',
        subtitleKey: 'machinery.hub.cards.vehicles.subtitle',
        descriptionKey: 'machinery.hub.cards.vehicles.description',
        path: '/machinery/vehicles',
        icon: <CarOutlined />,
        tagKeys: ['machinery.hub.tags.vehicle', 'machinery.hub.tags.user'],
      },
      {
        key: 'allocations',
        titleKey: 'machinery.hub.cards.allocations.title',
        subtitleKey: 'machinery.hub.cards.allocations.subtitle',
        descriptionKey: 'machinery.hub.cards.allocations.description',
        path: '/machinery/allocations',
        icon: <CalendarOutlined />,
        tagKeys: ['machinery.hub.tags.reservation', 'machinery.hub.tags.request'],
      },
      {
        key: 'assignments',
        titleKey: 'machinery.hub.cards.assignments.title',
        subtitleKey: 'machinery.hub.cards.assignments.subtitle',
        descriptionKey: 'machinery.hub.cards.assignments.description',
        path: '/machinery/assignments',
        icon: <TeamOutlined />,
        tagKeys: ['machinery.hub.tags.personnel', 'machinery.hub.tags.assignment'],
      },
    ],
  },
  {
    title: 'machinery.hub.maintenanceAndRecords',
    items: [
      {
        key: 'maintenance',
        titleKey: 'machinery.hub.cards.maintenance.title',
        subtitleKey: 'machinery.hub.cards.maintenance.subtitle',
        descriptionKey: 'machinery.hub.cards.maintenance.description',
        path: '/machinery/maintenance',
        icon: <SettingOutlined />,
        tagKeys: ['machinery.hub.tags.maintenance', 'machinery.hub.tags.record'],
      },
      {
        key: 'maintenance-types',
        titleKey: 'machinery.hub.cards.maintenanceTypes.title',
        subtitleKey: 'machinery.hub.cards.maintenanceTypes.subtitle',
        descriptionKey: 'machinery.hub.cards.maintenanceTypes.description',
        path: '/machinery/maintenance/types',
        icon: <BgColorsOutlined />,
        tagKeys: ['machinery.hub.tags.definition', 'machinery.hub.tags.class'],
      },
      {
        key: 'maintenance-rules',
        titleKey: 'machinery.hub.cards.maintenanceRules.title',
        subtitleKey: 'machinery.hub.cards.maintenanceRules.subtitle',
        descriptionKey: 'machinery.hub.cards.maintenanceRules.description',
        path: '/machinery/maintenance/rules',
        icon: <SettingOutlined />,
        tagKeys: ['machinery.hub.tags.period', 'machinery.hub.tags.threshold'],
      },
      {
        key: 'maintenance-alerts',
        titleKey: 'machinery.hub.cards.maintenanceAlerts.title',
        subtitleKey: 'machinery.hub.cards.maintenanceAlerts.subtitle',
        descriptionKey: 'machinery.hub.cards.maintenanceAlerts.description',
        path: '/machinery/maintenance/alerts',
        icon: <SettingOutlined />,
        tagKeys: ['machinery.hub.tags.alert', 'machinery.hub.tags.tracking'],
      },
    ],
  },
  {
    title: 'machinery.hub.formsAndFuel',
    items: [
      {
        key: 'forms',
        titleKey: 'machinery.hub.cards.forms.title',
        subtitleKey: 'machinery.hub.cards.forms.subtitle',
        descriptionKey: 'machinery.hub.cards.forms.description',
        path: '/machinery/forms',
        icon: <FormOutlined />,
        tagKeys: ['machinery.hub.tags.form', 'machinery.hub.tags.record'],
      },
      {
        key: 'fuel',
        titleKey: 'machinery.hub.cards.fuel.title',
        subtitleKey: 'machinery.hub.cards.fuel.subtitle',
        descriptionKey: 'machinery.hub.cards.fuel.description',
        path: '/machinery/fuel',
        icon: <CarOutlined />,
        tagKeys: ['machinery.hub.tags.tracking', 'machinery.hub.tags.import'],
      },
    ],
  },
];

const MachineryMain: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div style={{ padding: 4 }}>
      <Card bordered={false}>
        <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 20 }}>
          <Title level={3} style={{ margin: 0 }}>
            {t('machinery.hub.title')}
          </Title>
          <Paragraph style={{ marginBottom: 0 }}>
            {t('machinery.hub.description')}
          </Paragraph>
          <Text type="secondary">
            {t('machinery.hub.subtitle')}
          </Text>
        </Space>

        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          {hubSections.map(section => (
            <div key={section.title}>
              <Title level={5} style={{ marginTop: 0 }}>
                {t(section.title)}
              </Title>
              <Row gutter={[16, 16]}>
                {section.items.map(item => (
                  <Col xs={24} sm={12} lg={8} xl={6} key={item.key}>
                    <Card
                      hoverable
                      size="small"
                      title={
                        <Space>
                          {item.icon}
                          <span>{t(item.titleKey)}</span>
                        </Space>
                      }
                      extra={<Text type="secondary">{t(item.subtitleKey)}</Text>}
                    >
                      <Paragraph style={{ minHeight: 66, marginBottom: 12 }}>
                        {t(item.descriptionKey)}
                      </Paragraph>
                      <Space wrap size={[6, 6]} style={{ marginBottom: 12 }}>
                        {item.tagKeys.map(tagKey => (
                          <Tag key={tagKey}>{t(tagKey)}</Tag>
                        ))}
                      </Space>
                      <Button type="primary" block onClick={() => navigate(item.path)}>
                        {t('machinery.hub.goToScreen')}
                      </Button>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </Space>
      </Card>
    </div>
  );
};

export default MachineryMain;